import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, ArrowRight, Zap, Layers, Hammer, Palette, Grid3x3, Leaf, Droplets, Brush, Truck, Star, Home, Wrench } from 'lucide-react';

export default function LaerMerHandverk() {
  useEffect(() => {window.scrollTo(0, 0);}, []);
  const [activeTab, setActiveTab] = useState('intro');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const tabData = {
    intro: {
      title: 'Lær Mer',
      subtitle: 'Kunnskapsbase',
      desc: 'Materialer, håndverk og vedlikehold — en guide til hvordan vi bygger badstuer som varer',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/2b587728a_Dolomiti_interior_6.jpg'
    },
    tak: {
      title: 'Hvilket tak',
      subtitle: 'Artikkel 1',
      desc: 'Tre løsninger for ulike stedskontekster — presisjon, varme eller grønt',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/b5ed05aca_LykkeVervbier.jpg'
    },
    fasader: {
      title: 'Hvilken fasade',
      subtitle: 'Artikkel 2',
      desc: 'Kebony, oljet tre eller malt — tre måter å la materialet arbeide',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/7a4dd8d31_21.png'
    },
    innvendig: {
      title: 'Innvendige materialer',
      subtitle: 'Artikkel 3',
      desc: 'Panel, gulv, dører og vinduer — materialvalg som definerer opplevelsen inne',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9193d7b00_ThermoOr.jpg'
    },
    handverk: {

      title: 'Hvordan bygges',
      subtitle: 'Artikkel 3',
      desc: 'Fra konsept til montasje — presisjon møter håndverk',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/c855a9095_Handverk.jpg'
    },
    materialer: {
      title: 'Riktige materialer',
      subtitle: 'Artikkel 4',
      desc: 'Valg som påvirker vedlikehold, aldringsprocess og uttrykk',
      image: 'https://brookstimber.com/wp-content/uploads/2025/01/kebony25Body-1024x768.jpg'
    },
    transport: {
      title: 'Transport',
      subtitle: 'Artikkel 6',
      desc: 'Logistikk og sikker frakt av din badstue',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3bd6c37ff_d38bc4a79_Transport_House_Nomad35.jpg'
    },
    fundament: {
      title: 'Fundament',
      subtitle: 'Artikkel 5',
      desc: 'Grunnlaget for en solid badstue — konstruksjon som varer',
      image: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/ed6a66539_e90d3bf11_Assembling_House_Norway.jpg'
    }
  };

  const currentTab = tabData[activeTab];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative h-[600px] mb-0 overflow-hidden">
        
        <img
          src={currentTab.image}
          alt={currentTab.title}
          className="absolute inset-0 w-full h-full object-cover" />
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">{currentTab.subtitle}</p>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              {currentTab.title}
            </h1>
            <p className="font-body text-white/80 text-2xl max-w-2xl leading-relaxed">
              {currentTab.desc}
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* STICKY TABS */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="flex flex-nowrap gap-2 justify-center sticky top-0 bg-background py-6 z-40 border-b border-border/30 mx-auto px-6 md:px-12">
          {[
          { id: 'intro', label: 'Start', icon: 'star' },
          { id: 'tak', label: 'Tak', icon: 'home' },
          { id: 'fasader', label: 'Fasader', icon: 'palette' },
          { id: 'innvendig', label: 'Innvendig', icon: 'layers' },
          { id: 'handverk', label: 'Håndverk', icon: 'hammer' },
          { id: 'materialer', label: 'Materialer', icon: 'layers' },
          { id: 'transport', label: 'Transport', icon: 'truck' },
          { id: 'fundament', label: 'Fundament', icon: 'wrench' }].
          map((tab) =>
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-body text-2xl tracking-widest uppercase transition-all rounded-[4px] flex items-center gap-2 ${
            activeTab === tab.id ?
            'bg-[#2a1810] text-white' :
            'text-foreground/60 hover:text-foreground'}`
            }>
            
              <span className="text-base">
                {tab.icon === 'truck' ? <Truck size={18} /> :
              tab.icon === 'star' ? <Star size={18} /> :
              tab.icon === 'home' ? <Home size={18} /> :
              tab.icon === 'palette' ? <Palette size={18} /> :
              tab.icon === 'hammer' ? <Hammer size={18} /> :
              tab.icon === 'layers' ? <Layers size={18} /> :
              tab.icon === 'wrench' ? <Wrench size={18} /> : tab.icon}
              </span>
              {tab.label}
            </button>
          )}
        </motion.div>

        {/* INTRO */}
        {activeTab === 'intro' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16">
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">
                  Forstå materialer,<br />håndverk og valg
                </h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                  En badstue står aldri helt alene. Den leses sammen med terrenget, lyset og naturen rundt. Vi arbeider ikke med ett fasitsvar, men med materialer som kan tilpasses stedet.
                </p>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Valg av tak, fasade og materialer er en del av arkitekturen. De avgjør hvordan badstuen eldes, hvor mye vedlikehold som kreves, og hvordan den oppleves gjennom året.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6 mb-16">
                {[
              { title: 'Tak', desc: 'Tre løsninger for ulike stedskontekster', icon: <Zap className="w-6 h-6" />, link: 'tak' },
              { title: 'Fasader', desc: 'Materiale som bestemmer aldringsprocess', icon: <Palette className="w-6 h-6" />, link: 'fasader' },
              { title: 'Håndverk', desc: '3D, BIM og presis produksjon', icon: <Hammer className="w-6 h-6" />, link: 'handverk' },
              { title: 'Materialer', desc: 'Valg som påvirker vedlikehold og uttrykk', icon: <Layers className="w-6 h-6" />, link: 'materialer' }].
              map((item, idx) =>
              <motion.button
                key={idx}
                onClick={() => setActiveTab(item.link)}
                whileHover={{ y: -4 }}
                className="text-left bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all group">
                
                    <div className="text-[#2d5a3d] mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                    <h3 className="text-[#2a1810] mb-2 text-2xl font-bold">{item.title}</h3>
                    <p className="text-foreground/60 text-2xl font-body">{item.desc}</p>
                  </motion.button>
              )}
                <motion.button
                onClick={() => setActiveTab('transport')}
                whileHover={{ y: -4 }}
                className="text-left bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all group">
                
                  <div className="text-[#2d5a3d] mb-4 group-hover:scale-110 transition-transform"><Truck className="w-6 h-6" /></div>
                  <h3 className="text-[#2a1810] mb-2 text-2xl font-bold">Transport</h3>
                  <p className="text-foreground/60 text-lg font-body">Logistikk og sikker frakt av din badstue</p>
                </motion.button>
                <motion.button
                onClick={() => setActiveTab('fundament')}
                whileHover={{ y: -4 }}
                className="text-left bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all group">
                
                  <div className="text-[#2d5a3d] mb-4 group-hover:scale-110 transition-transform"><Layers className="w-6 h-6" /></div>
                  <h3 className="text-[#2a1810] mb-2 text-2xl font-bold">Fundament</h3>
                  <p className="text-foreground/60 text-lg font-body">Grunnlaget for en solid og langvarig konstruksjon</p>
                </motion.button>
              </div>

              <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8">
                <p className="font-heading text-2xl tracking-widest uppercase text-[#2d5a3d] mb-2">5 Artikler</p>
                <p className="text-foreground/70 text-2xl font-body leading-relaxed">Disse guidene forklarer hvordan vi tenker om tak og fasader, hvordan vi arbeider med 3D-modellering og prefabrikasjon, hvordan materialene kan følges opp over tid, og hva som gjør fundamentet sterkt og varig.

              </p>
              </div>
            </div>
          </motion.section>
        }

        {/* FASADER SECTION */}
        {activeTab === 'fasader' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 2</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Hvilken fasade bør du velge?</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Fasaden bestemmer både hvordan badstuen eldes og hvor mye vedlikehold den krever. Tre materialer, tre måter å møte tiden på.
                </p>
              </motion.div>

              <div className="mb-12">
               <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Tre fasadematerialer</h3>
               <div className="flex flex-col md:flex-row gap-6">
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all">
                    <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/b81fd725d_KebonyRadiata.jpg" alt="Kebony" className="w-full h-72 object-cover" />
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">1</span>
                      </div>
                      <h4 className="font-heading text-2xl font-light text-[#2a1810] mb-4">Kebony</h4>
                      <p className="font-body text-foreground/70 leading-relaxed text-2xl">Varmbehandlet tre som gråner naturlig til sølvgrått. Minimal vedlikehold og lang levetid. Perfekt for deg som ønsker naturlig aldringsprocess.</p>
                      <motion.a href="/kebony-kled" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all">
                    <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/0c391f3d4_Oljetkledning.png" alt="Oljet tre" className="w-full h-72 object-cover" />
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">2</span>
                      </div>
                      <h4 className="font-heading text-2xl font-light text-[#2a1810] mb-4">Oljet tre</h4>
                      <p className="font-body text-foreground/70 leading-relaxed text-2xl">Tre behandlet med naturlige oljer som bevarer den naturlige fargen. Krever jevnlig vedlikehold, men gir varm og naturlig overflate som endres subtilt over tid.</p>
                      <motion.a href="/oljet-tre" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all">
                    <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/65e07f80b_Maltkledning.png" alt="Malt fasade" className="w-full h-72 object-cover" />
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">3</span>
                      </div>
                      <h4 className="font-heading text-2xl font-light text-[#2a1810] mb-4">Malt fasade</h4>
                      <p className="font-body text-foreground/70 leading-relaxed text-2xl">Høykvalitetsmaling gir deg kontroll over fargen og uttrykket. Krever regelmessig vedlikehold, men lar deg velge både farve og hvor tydelig badstuen skal tre frem.</p>
                      <motion.a href="/eksteriormaling" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.section>
        }

        {/* TAK SECTION */}
        {activeTab === 'tak' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 1</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Hvilket tak bør du velge?</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Taket gjør mer enn å holde vann ute. Det bestemmer silhuetten, reflekterer lys og påvirker hvordan badstuen møter vær og vegetasjon.
                </p>
              </motion.div>

              <div className="mb-12">
                <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Tre taktyper, tre måter å møte stedet på</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all flex flex-col">
                    <img src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/2caf4cdef_Metalroof.jpg" alt="Stående fals i metall" className="w-full h-72 object-cover" />
                    <div className="p-8 flex flex-col flex-1">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">1</span>
                      </div>
                      <h4 className="text-[#2a1810] mb-4 text-2xl font-bold">Stående fals i metall</h4>
                      <p className="text-foreground/70 text-2xl font-body leading-relaxed flex-1">Presist, rolig og tydelig. Gir en presis og sammenhengende takflate. Vi tilbyr svart matt, lys grå og grafitt, tre rolige farger som fungerer godt mot både mørk skog, berg og bynære omgivelser.</p>
                      <motion.a href="/roofing-metal" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all flex flex-col">
                    <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/5aa9e97ea_28mmtermotre.jpg" alt="28 mm termotre" className="w-full h-72 object-cover" />
                    <div className="p-8 flex flex-col flex-1">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">2</span>
                      </div>
                      <h4 className="text-[#2a1810] mb-4 text-2xl font-bold">Tretak - 28 mm termotre</h4>
                      <p className="text-foreground/70 text-2xl font-body leading-relaxed flex-1">Varmt, taktilt og naturbundet. Har en tydelig materialitet og varmere overflate. Det gjør taket mer taktilt og lar badstuen tre frem som et trebygget volum. Behandlingen gjør treet mer formstabilt, en fordel i nordisk klima.</p>
                      <motion.a href="/roofing-wood" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                  <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all flex flex-col">
                    <img src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/720bf7692_GreenRoof.jpg" alt="Grønt tak" className="w-full h-72 object-cover" />
                    <div className="p-8 flex flex-col flex-1">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">3</span>
                      </div>
                      <h4 className="text-[#2a1810] mb-4 text-2xl font-bold">Grønt tak</h4>
                      <p className="text-foreground/70 text-2xl font-body leading-relaxed flex-1">Mykt, landskapsnært og dempet. Er riktig når badstuen skal ligge lavt eller gli stille inn i naturen. Det gir bygget en mykere avslutning og bindinger til landskapet.</p>
                      <motion.a href="/roofing-green" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                        LÆR MER
                      </motion.a>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.section>
        }

        {/* INNVENDIG MATERIALER SECTION */}
        {activeTab === 'innvendig' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 3</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Innvendige materialer</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Materialene inne i badstuen påvirker både estetikken, varmekomforten og vedlikeholdet. Her er en oversikt over valgene du kan ta.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
              {
                title: 'Innvendige Panel',
                desc: 'Or, Abachi, Termo Asp, Termo Radiata STS4 eller Sort Asp STS4 IGNITE — valget av panel bestemmer temperaturfølelse og uttrykk inne i badstuen.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9193d7b00_ThermoOr.jpg',
                num: '1',
                href: '/innvendig-panel'
              },
              {
                title: 'Gulv',
                desc: 'Behandlet tre, steinflis eller varmeledende gulv — ulike gulvvalg gir forskjellig komfort og vedlikeholdsbehov.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f1d8f1215_Tregulv.png',
                num: '2',
                href: '/innvendig-gulv'
              },
              {
                title: 'Dører',
                desc: 'Klart glass, bronsetonet glass eller massiv tredør — døren setter tonen for inntrykket og privatlivet.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8f98ae849_Glassdor.png',
                num: '3',
                href: '/innvendig-dorer'
              },
              {
                title: 'Vindu (Okno)',
                desc: 'Ingen vindu, lite vindu eller panoramavindu — utsikt og lys som tilpasses plassering og ønske.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8984f902f_BrownGlass.png',
                num: '4',
                href: '/innvendig-vindu'
              },
              {
                title: 'Belysning og Lydanlegg',
                desc: 'Fra enkel LED til luksus RGB og JBL-lyd — stemningssetting med lys og musikk for den perfekte opplevelsen.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/16d3dd2b9_RGB.png',
                num: '5',
                href: '/innvendig-belysning'
              },
              {
                title: 'Ekstra',
                desc: 'Håndklestativ, ryggstøtte eller komplett pakke — tillegg som gjør badstuen enda mer komfortabel.',
                img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/1fc3e047b_Brown.png',
                num: '6',
                href: '/innvendig-ekstra'
              }].
              map((item, idx) =>
              <motion.a key={idx} href={item.href} whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all block">
                    <img src={item.img} alt={item.title} className="w-full h-56 object-cover" />
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-full bg-[#2d5a3d]/10 flex items-center justify-center mb-4">
                        <span className="text-xl font-heading font-light text-[#2d5a3d]">{item.num}</span>
                      </div>
                      <h4 className="font-heading text-lg font-light text-[#2a1810] mb-4">{item.title}</h4>
                      <p className="font-body text-2xl text-foreground/70 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.a>
              )}
              </div>
            </div>
          </motion.section>
        }

        {/* HANDVERK SECTION */}
        {activeTab === 'handverk' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 3</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Hvordan bygges en LYKKE badstue?</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  En badstue kan virke enkel i formen. Nettopp derfor må alt under overflaten være nøye tenkt. Vi kombinerer tradisjonell materialforståelse med digitale verktøy og presis produksjon.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6 mb-16">
                <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all">
                  <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/ae8bb72cb_LYKKENOMAD.png" alt="Arkitektur" className="w-full h-72 object-cover" />
                  <div className="p-8">
                    <div className="w-12 h-12 rounded-full bg-[#2a1810] text-white flex items-center justify-center mb-4 font-heading font-light text-lg">
                      A
                    </div>
                    <h4 className="font-heading text-lg font-light text-[#2a1810] mb-4">Arkitektur</h4>
                    <p className="font-body text-2xl text-foreground/70 leading-relaxed">Form, volum og forhold til stedet. Arkitektur som møter landskapet.</p>
                    <motion.a href="/arkitektur" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                      LÆR MER
                    </motion.a>
                  </div>
                </motion.div>
                <motion.div whileHover={{ y: -4 }} className="bg-white border border-border/20 rounded-[8px] overflow-hidden hover:shadow-lg hover:border-[#2d5a3d] transition-all">
                  <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/cd8788aa9_Handverk.jpg" alt="Håndverk" className="w-full h-72 object-cover" />
                  <div className="p-8">
                    <div className="w-12 h-12 rounded-full bg-[#2a1810] text-white flex items-center justify-center mb-4 font-heading font-light text-lg">
                      H
                    </div>
                    <h4 className="font-heading text-lg font-light text-[#2a1810] mb-4">Håndverk</h4>
                    <p className="font-body text-2xl text-foreground/70 leading-relaxed">Presisjon i detaljer og sammensetning. Håndverk møter digital produksjon.</p>
                    <motion.a href="/handverket" className="inline-flex items-center justify-center w-full mt-6 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[8px] hover:bg-[#2d5a3d]/90 transition-all">
                      LÆR MER
                    </motion.a>
                  </div>
                </motion.div>
              </div>

              <div className="space-y-5 mb-16">
                {[
              { phase: 'Konsept', desc: 'Plassering, volum og forhold til stedet avklares', icon: '1' },
              { phase: '3D og BIM', desc: 'Detaljer, konstruksjon og grensesnitt koordineres digitalt', icon: '2' },
              { phase: 'Produksjon', desc: 'Komponenter fremstilles med CNC presisjon', icon: '3' },
              { phase: 'Prefabrikasjon', desc: 'Elementer settes sammen og kontrolleres før levering', icon: '4' },
              { phase: 'Montasje', desc: 'Badstuen monteres og avsluttes på stedet', icon: '5' }].
              map((item, idx) =>
              <motion.div
                key={idx}
                whileHover={{ y: -4 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                className="bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all">
                
                    <div className="w-12 h-12 rounded-full bg-[#2a1810] text-white flex items-center justify-center font-heading font-light text-lg mb-4">
                      {item.icon}
                    </div>
                    <h3 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.phase}</h3>
                    <p className="font-body text-2xl text-foreground/60">{item.desc}</p>
                  </motion.div>
              )}
              </div>

              <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12 mb-12">
                <h3 className="font-heading text-3xl font-light mb-6">Presisjon møter håndverk</h3>
                <p className="font-body text-2xl text-white/80 leading-relaxed mb-6">
                  Digital produksjon og CNC brukes der repetisjon og nøyaktighet er viktig. Det gjør det mulig å fremstille komponenter med høy presisjon. Men teknologien erstatter ikke håndverket, den støtter det.
                </p>
                <p className="font-body text-2xl text-white/80 leading-relaxed">
                  Det er fortsatt materialforståelsen, sammensettingen og monteringen som avgjør resultatet. I denne kombinasjonen av maskinell nøyaktighet og menneskelig vurdering oppstår kvaliteten.
                </p>
              </div>

              <motion.a whileHover={{ scale: 1.02 }} href="/handverket" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
                Dypere om Håndverket <ArrowRight size={16} />
              </motion.a>
            </div>
          </motion.section>
        }

        {/* TRANSPORT SECTION */}
        {activeTab === 'transport' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 6</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Transport, sikker frakt av din badstue</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  En badstue er et større prosjekt. Vi håndterer all logistikk og sikrer at badstuen din ankommer i perfekt stand, uansett hvor du befinner deg.
                </p>
              </motion.div>

              <motion.div
              whileHover={{ y: -4 }}
              className="text-left bg-white border border-border/20 rounded-[8px] overflow-hidden hover:border-[#2d5a3d] hover:shadow-md transition-all mb-16">
              
                <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/38564b986_SaunaKraning.jpg" alt="Transport" className="w-full h-64 object-cover" />
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-light mb-3 text-[#2a1810]">Fra produksjon til montasje</h3>
                  <p className="font-body text-2xl text-foreground/70 mb-6 leading-relaxed">
                    Vi administrerer hele transportprosessen, fra pakking på fabrikken til levering på stedet. Badstuen din behandles med omhu og sikres godt for en trygg reise.
                  </p>
                  <motion.a whileHover={{ scale: 1.02 }} href="/transport" className="inline-flex items-center gap-2 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2d5a3d]/90 transition-all">
                    LÆR MER <ArrowRight size={16} />
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </motion.section>
        }

        {/* FUNDAMENT SECTION */}
        {activeTab === 'fundament' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 5</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Fundament, grunnlaget for kvalitet</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  En badstue er kun så solid som fundamentet den står på. Uansett hvor vakker arkitekturen er og hvor gode materialene er, må konstruksjonen være grundig og pålitelig.
                </p>
              </motion.div>

              <motion.div
              whileHover={{ y: -4 }}
              className="text-left bg-white border border-border/20 rounded-[8px] overflow-hidden hover:border-[#2d5a3d] hover:shadow-md transition-all mb-16">
              
                <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/d27418b70_Zrzutekranu2026-04-11o164523.png" alt="Fundament" className="w-full h-64 object-cover" />
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-light mb-3 text-[#2a1810]">Fra analyse til ferdig fundament</h3>
                  <p className="font-body text-2xl text-foreground/70 mb-6 leading-relaxed">
                    En fullstendig guide gjennom prosessen av å forberede, velge og utføre det rette fundamentet for din badstue.
                  </p>
                  <motion.a whileHover={{ scale: 1.02 }} href="/fundament" className="inline-flex items-center gap-2 px-6 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2d5a3d]/90 transition-all">
                    LÆR MER <ArrowRight size={16} />
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </motion.section>
        }

        {/* MATERIALER SECTION */}
        {activeTab === 'materialer' &&
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-16">
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Artikkel 4</p>
                <h2 className="font-heading text-5xl font-light leading-[1.1] mb-6 text-[#2a1810]">Hvordan velger du riktige materialer?</h2>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                  Konfiguratoren er en måte å velge hvordan badstuen skal leve over tid. Når du velger tak, fasade og overflater, velger du samtidig hvor tydelig bygget skal tre frem og hvor mye vedlikehold du ønsker.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6 mb-16">
                {[
              { title: 'Stående fals i metall', desc: 'Stabilt uttrykk og lite vedlikehold', icon: <Grid3x3 className="w-6 h-6" /> },
              { title: 'Termotre', desc: 'Rolig karakter med naturlig vedlikehold', icon: <Layers className="w-6 h-6" /> },
              { title: 'Grønt tak', desc: 'Del av landskapet, sesongvis oppfølging', icon: <Leaf className="w-6 h-6" /> },
              { title: 'Kebony', desc: 'Sølvgrå patina med minimal vedlikehold', icon: <Droplets className="w-6 h-6" /> },
              { title: 'Oljet tre', desc: 'Bevart tone, jevnlig olje-vedlikehold', icon: <Brush className="w-6 h-6" /> },
              { title: 'Malt fasade', desc: 'Kontrollert farge med regelmessig vedlikehold', icon: <Palette className="w-6 h-6" /> }].
              map((item, idx) =>
              <motion.div
                key={idx}
                whileHover={{ y: -4 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                className="bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all group">
                
                    <div className="text-[#2d5a3d] mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                    <h3 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.title}</h3>
                    <p className="font-body text-2xl text-foreground/60">{item.desc}</p>
                  </motion.div>
              )}
              </div>

              <div className="space-y-5 mb-12">
                <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Tre viktige spørsmål</h3>
                {[
              { q: 'Hus eller landskap?', a: 'En badstue ved et moderne hus kan tåle større kontrast. Et bygg nær skog kan ha godt av roligere materialuttrykk som gråner eller binder seg til vegetasjonen.' },
              { q: 'Hvor mye vedlikehold?', a: 'Noen ønsker materialer som eldes fritt. Andre vil bevare bestemt farge og er komfortable med oppfølging. Det riktige valget handler også om ditt forhold til bygget over tid.' },
              { q: 'Hva er viktig for deg?', a: 'Godt vedlikehold er enkelt når det skjer jevnlig. En rolig vår- eller høstkontroll gir ofte nok informasjon til å fange opp små ting før de blir store.' }].
              map((item, idx) =>
              <div key={idx} className="bg-white border border-border/20 rounded-[6px] p-6 hover:border-[#2d5a3d] transition-colors">
                    <h4 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.q}</h4>
                    <p className="font-body text-2xl leading-relaxed text-foreground/70">{item.a}</p>
                  </div>
              )}
              </div>

              <div className="bg-[#2d5a3d] text-white rounded-[8px] p-12 mb-12">
                <p className="font-body text-2xl leading-relaxed mb-6">
                  Hvis du allerede har blitt kjent med vår guide, vet du nå sikkert hvilke valg du vil gjøre. Vi inviterer deg til våre produktlinjer, velg en sauna og konfigurér den i vår konfigurator.
                </p>
                <p className="font-body text-2xl leading-relaxed mb-8">
                  Hvis du derimot føler at du trenger hjelp, skriv til oss gjerne, vi vil forklare alt i detalj.
                </p>
                <motion.a whileHover={{ scale: 1.04 }} href="/om-oss#kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all">
                  KONTAKT OSS
                </motion.a>
              </div>
              <div className="flex flex-row gap-4 justify-center flex-wrap">
               <motion.a whileHover={{ scale: 1.02 }} href="/sauna-ly" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
                 LYKKE LY <ArrowRight size={16} />
               </motion.a>
               <motion.a whileHover={{ scale: 1.02 }} href="/sauna-panorama" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
                 LYKKE PANORAMA <ArrowRight size={16} />
               </motion.a>
               <motion.a whileHover={{ scale: 1.02 }} href="/sauna-fjel" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
                 LYKKE FJELL <ArrowRight size={16} />
               </motion.a>
              </div>
            </div>
          </motion.section>
        }

      </div>

      <Footer />
    </div>);

}